<template>
  <v-card dark v-if="editing" class="edit profile-qualification mt-2" key="editing">
    <can I="edit" a="ProfileQualification">
      <v-card-title>
        <div>
          <div class="title">
            {{ profileQualification.qualificationTypeName }}
            <span v-if="profileQualification.specialization">
              ({{ profileQualification.specialization }})
            </span>
          </div>
          <div class="subheading grey--text">
            {{ profileQualification.institutionName }}
          </div>
        </div>
        <v-spacer />

        <v-tooltip v-if="value.isFeatured" left>
          <v-btn small flat fab slot="activator" color="yellow lighten-1">
            <v-icon>mdi-star</v-icon>
          </v-btn>
          <span>Featured qualification</span>
        </v-tooltip>
      </v-card-title>

      <v-alert color="error" transition="scale-transition" :value="profileQualification.errors">
        <ul>
          <li v-for="message in profileQualification.errors" :key="message">{{ message }}</li>
        </ul>
      </v-alert>

      <v-card-text>
        <v-form v-model="valid">
          <QualificationSelect
            :value="profileQualification.qualification"
            @input="setQualification"
            :rules="rules.qualification"
            required
            hint="You must select a qualification"
          />

          <date-picker
            label="Starting Date"
            v-model="profileQualification.startsAt"
            :rules="rules.startsAt"
            required
            append-icon="mdi-calendar-today"
          />
          <date-picker
            label="End Date"
            v-model="profileQualification.endsAt"
            :rules="rules.endsAt"
            required
            append-icon="mdi-calendar"
          />
          <date-picker
            label="Accreditation Date"
            v-model="profileQualification.accreditedAt"
            :rules="rules.accreditedAt"
            clearable
            append-icon="mdi-calendar-star"
          />

          <GradeSelect
            v-if="profileQualification.institutionName"
            :value="profileQualification"
            @input="setGrade"
          />

          <DocumentUpload
            v-if="profileQualification && profileQualification.id"
            label="Proof of Registration"
            :action="
              `/admin/profile_qualifications/${profileQualification.id}/proof_of_registration`
            "
            :value="profileQualification.proofOfRegistration"
            auto-submit
            @submit="
              $store.dispatch('admin/profileQualifications/search', {
                profile: { id: $route.params.id },
              })
            "
          />
          <DocumentUpload
            v-if="profileQualification && profileQualification.id"
            label="Transcript"
            :action="`/admin/profile_qualifications/${profileQualification.id}/transcript`"
            :value="profileQualification.transcript"
            auto-submit
            @submit="
              $store.dispatch('admin/profileQualifications/search', {
                profile: { id: $route.params.id },
              })
            "
          />

          <v-checkbox
            v-model="profileQualification.accessRestricted"
            color="deep-orange"
            :label="
              `Restrict access to documents: ${
                profileQualification.accessRestricted ? 'Restricted' : 'Downloadable'
              }`
            "
          ></v-checkbox>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions v-if="profileQualification">
        <v-spacer />
        <v-btn flat @click="cancel" color="grey">Cancel</v-btn>
        <v-btn color="primary" :disabled="!valid" @click="submit">
          {{ profileQualification.id ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </can>
  </v-card>

  <v-card dark v-else class="profile-qualification card mt-2" key="viewing">
    <can I="read" a="ProfileQualification">
      <v-card-title>
        <v-avatar class="elevation-0 mr-3" v-if="value.institution">
          <img :src="value.institution.logo" />
        </v-avatar>
        <div>
          <div class="title">
            {{ value.qualificationTypeName }}
            <span v-if="value.specialization"> ({{ value.specialization }}) </span>
          </div>
          <div class="subheading grey--text">
            {{ value.institutionName }}
          </div>
        </div>

        <v-spacer />

        <Flag v-for="(f, i) in flags" :key="`qualification-flag-${i}`" :value="f" icon />
        <can I="create" a="Flag">
          <CreateFlag
            :target="{ ...profileQualification, type: 'ProfileQualification' }"
            :profile="profile"
            :filter="['grade_value', 'qualification_dates', 'transcript', 'qualification']"
            @done="$emit('refresh')"
          >
            <v-btn icon flat color="error" slot="activator">
              <v-icon>mdi-flag</v-icon>
            </v-btn>
          </CreateFlag>
        </can>
        <v-tooltip v-if="value.isFeatured" left>
          <v-btn small flat fab slot="activator" color="yellow lighten-1">
            <v-icon>mdi-star</v-icon>
          </v-btn>
          <span>Featured qualification</span>
        </v-tooltip>
        <v-tooltip v-if="value.verifiedAt && value.administrator" left>
          <v-btn small flat fab slot="activator" color="success lighten-1">
            <v-icon>mdi-check</v-icon>
          </v-btn>
          <span>
            Verified by
            {{ value.administrator.firstName }} {{ value.administrator.lastName }}
            {{ new Date(value.verifiedAt) | inWords({ addSuffix: true }) }}
          </span>
        </v-tooltip>
      </v-card-title>

      <v-divider />

      <!-- Attributes table -->
      <table class="v-table theme--dark">
        <tbody>
          <tr v-if="profileQualification.id">
            <th class="text-xs-right">ID</th>
            <td>{{ profileQualification.id }}</td>
          </tr>

          <tr>
            <th class="text-xs-right">Qualification</th>
            <td>
              <router-link :to="`/qualifications/${profileQualification.qualification.id}`">
                <span
                  v-if="
                    profileQualification &&
                      profileQualification.qualificationType &&
                      profileQualification.qualificationType.acronym
                  "
                >
                  {{ profileQualification.qualificationType.acronym }}
                </span>
                <span v-else> {{ profileQualification.qualificationTypeName }} </span>
                <em v-if="profileQualification.qualification.specialization">in</em>
                {{ profileQualification.qualification.specialization }}
                <em>from</em>
                <span
                  v-if="
                    profileQualification &&
                      profileQualification.institution &&
                      profileQualification.institution.acronym
                  "
                >
                  {{ profileQualification.institution.acronym }}
                </span>
                <span v-else> {{ profileQualification.institutionName }} </span>
              </router-link>
            </td>
          </tr>

          <tr>
            <th class="text-xs-right">Qualification Type</th>
            <td
              v-if="
                profileQualification.qualificationType && profileQualification.qualificationType.id
              "
            >
              <router-link
                :to="`/qualification_types/${profileQualification.qualificationType.id}`"
              >
                {{ profileQualification.qualificationTypeName }}
              </router-link>
            </td>
            <td v-else>{{ profileQualification.qualificationTypeName }}</td>
          </tr>
          <tr>
            <th class="text-xs-right">Specialization</th>
            <td v-if="profileQualification.specialization">
              {{ profileQualification.specialization }}
            </td>
            <td v-else><em>None</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Institution</th>
            <td v-if="profileQualification.institution && profileQualification.institution.id">
              <router-link :to="`/institutions/${profileQualification.institution.id}`">
                {{ profileQualification.institutionName }}
              </router-link>
            </td>
            <td v-else>{{ profileQualification.institutionName }}</td>
          </tr>
          <tr>
            <th class="text-xs-right">Started</th>
            <td v-if="profileQualification.startsAt">
              {{ new Date(profileQualification.startsAt) | date('d MMMM yyyy') }}
              ({{ new Date(profileQualification.startsAt) | inWords({ addSuffix: true }) }})
            </td>
            <td v-else><em>Not set</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Completed</th>
            <td v-if="profileQualification.endsAt">
              {{ new Date(profileQualification.endsAt) | date('d MMMM yyyy') }}
              ({{ new Date(profileQualification.endsAt) | inWords({ addSuffix: true }) }})
            </td>
            <td v-else><em>Not set</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Accredited</th>
            <td v-if="profileQualification.accreditedAt">
              {{ new Date(profileQualification.accreditedAt) | date('d MMMM yyyy') }}
              ({{ new Date(profileQualification.accreditedAt) | inWords({ addSuffix: true }) }})
            </td>
            <td v-else><em>Not set</em></td>
          </tr>

          <tr>
            <th class="text-xs-right">Grade</th>
            <td v-if="profileQualification.gradeValue">
              {{ profileQualification.gradeValue }} ({{ profileQualification.gradeScale }})
            </td>
            <td v-else><em>Not set</em></td>
          </tr>

          <tr>
            <th class="text-xs-right">Proof of Registration</th>
            <td v-if="profileQualification.proofOfRegistration">
              <a :href="profileQualification.proofOfRegistration.url" target="_blank">
                View proof of registration
              </a>
            </td>
            <td v-else><em>Not provided</em></td>
          </tr>

          <tr>
            <th class="text-xs-right">Transcript</th>
            <td v-if="profileQualification.transcript">
              <a :href="profileQualification.transcript.url" target="_blank">
                View transcript
              </a>
            </td>
            <td v-else><em>Not provided</em></td>
          </tr>

          <tr>
            <th class="text-xs-right">Access Restricted</th>
            <td>{{ profileQualification.accessRestricted ? 'Yes' : 'No' }}</td>
          </tr>

          <tr>
            <th class="text-xs-right">Last updated</th>
            <td>
              {{ new Date(profileQualification.updatedAt) | date('h:mm a, MMMM do yyyy') }}
              ({{ new Date(profileQualification.updatedAt) | inWords({ addSuffix: true }) }})
            </td>
          </tr>

          <tr>
            <th class="text-xs-right">Created</th>
            <td>
              {{ new Date(profileQualification.createdAt) | date('h:mm a, MMMM do yyyy') }}
              ({{ new Date(profileQualification.createdAt) | inWords({ addSuffix: true }) }})
            </td>
          </tr>
        </tbody>
      </table>

      <v-divider />

      <v-card-actions>
        <can I="edit" a="ProfileQualification">
          <v-combobox
            v-if="addingComponent"
            label="Type description or select from options"
            v-model="componentSearch"
            prepend-icon="mdi-close"
            @click:prepend="cancelComponentSearch"
            :items="filteredQualificationComponents"
            item-text="description"
            @input="createComponent"
          >
            <template slot="item" slot-scope="data">
              <v-list-tile-content>
                <v-list-tile-title v-html="data.item.description" />
                <v-list-tile-sub-title v-html="data.item.reference" />
              </v-list-tile-content>
            </template>
          </v-combobox>
          <v-btn v-else flat color="secondary" @click="addingComponent = true">
            <v-icon left>mdi-plus</v-icon> Add qualification component
          </v-btn>
        </can>

        <v-spacer />

        <can I="delete" a="ProfileQualification">
          <v-dialog v-model="deleteDialog" max-width="600">
            <v-btn flat slot="activator" color="error">
              <v-icon left>mdi-delete</v-icon> Delete
            </v-btn>

            <v-card dark>
              <v-card-title class="headline">
                Are you sure?
              </v-card-title>
              <v-card-text>
                <p>
                  This action cannot be undone and will permanently remove this profile
                  qualification.
                </p>
                <p>
                  Any associated proof of registration and transcript documents will also be
                  deleted.
                </p>
              </v-card-text>
              <v-card-actions>
                <v-btn flat block color="grey" @click="deleteDialog = false">cancel</v-btn>
                <v-btn block color="error" @click="destroy">Delete</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </can>
        <can I="edit" a="ProfileQualification">
          <v-btn color="primary" @click="editing = true">
            <v-icon left>mdi-pencil</v-icon> Edit
          </v-btn>
        </can>
        <can I="verify" a="ProfileQualification">
          <v-btn
            v-if="!value.verifiedAt && value.transcript && value.gradeValue"
            color="success"
            @click="verify(profileQualification)"
          >
            Verify
          </v-btn>
        </can>
      </v-card-actions>

      <v-data-table
        v-if="value.components && value.components.length"
        :items="value.components"
        :headers="componentHeaders"
      >
        <template slot="items" slot-scope="props">
          <tr>
            <td>{{ props.item.reference }}</td>
            <td>{{ props.item.description }}</td>
            <td>{{ props.item.gradeValue }}</td>
            <td class="text-xs-right">
              <v-tooltip left>
                <v-btn
                  small
                  flat
                  fab
                  slot="activator"
                  color="error"
                  @click.native="removeComponent(props.item)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
                <span>Delete</span>
              </v-tooltip>

              <v-tooltip left>
                <v-btn
                  small
                  flat
                  fab
                  slot="activator"
                  color="secondary"
                  @click.native="componentBeingEdited = props.item"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <span>Edit</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>

      <v-dialog :value="componentBeingEdited" max-width="600">
        <v-card v-if="componentBeingEdited" dark>
          <v-card-title>
            <div>
              <div class="title">{{ componentBeingEdited.description }}</div>
              <span class="subtitle grey--text">{{ componentBeingEdited.reference }}</span>
            </div>
          </v-card-title>
          <v-card-text>
            <v-form v-model="componentValid">
              <v-autocomplete
                label="Qualification Component"
                v-model="componentBeingEdited.qualificationComponent"
                :items="filteredQualificationComponents"
                item-text="description"
                item-value="id"
                @input="setComponentDescription"
                return-object
                dark
              />
              <v-text-field
                label="Reference"
                v-model="componentBeingEdited.reference"
                :rules="[]"
              />
              <v-text-field
                label="Description"
                v-model="componentBeingEdited.description"
                :rules="rules.component.description"
                required
              />
              <GradeSelect :value="componentBeingEdited" @input="setComponentGrade" />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn flat @click.native="componentBeingEdited = null" color="grey">cancel</v-btn>
            <v-btn color="primary" @click="updateComponent">Update</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </can>
  </v-card>
</template>

<script>
// Components
import CreateFlag from '@/components/flags/CreateFlag'
import DocumentUpload from '@/components/DocumentUpload'
import Flag from '@/components/flags/Flag'
import GradeSelect from '@/components/selects/GradeSelect'
import QualificationSelect from '@/components/selects/QualificationSelect'

// Helpers
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'

// Vue Component
export default {
  components: { CreateFlag, DocumentUpload, Flag, GradeSelect, QualificationSelect },
  props: {
    value: { type: Object, default: null },
    flags: { type: Array, default: () => [] },
  },
  created() {
    const institution = this.profileQualification.institution
    const qualificationType = this.profileQualification.qualificationType
    this.$store.dispatch('admin/qualificationComponents/fetchAll', {
      institution,
      qualificationType,
    })
  },
  data() {
    return {
      // Toggles
      editing: this.value.id == null,
      selectingInstitution: this.value.id == null || !!this.value.institution,
      selectingQualification: this.value.id == null || !!this.value.qualification,
      addingComponent: false,
      deleteDialog: false,
      componentSearch: '',
      // Model
      profileQualification: JSON.parse(
        JSON.stringify(Object.assign({ qualification: {} }, this.value))
      ),
      // Validations
      valid: this.value.id != null,
      rules: {
        institutionName: [v => !!v || 'must be provided'],
        qualificationTypeName: [v => !!v || 'must be provided'],
        qualification: [v => !!v || 'must be provided'],
        startsAt: [v => !!v || 'must be provided'],
        endsAt: [v => !!v || 'must be provided'],
        accreditedAt: [],
        component: {
          description: [v => !!v || 'must be provided'],
        },
      },
      // Component Model
      componentBeingEdited: null,
      componentValid: false,
      componentHeaders: [
        { text: 'Reference', value: 'reference' },
        { text: 'Description', value: 'description' },
        { text: 'Grade', value: 'gradeNormalized' },
        { text: 'Actions', sortable: false, align: 'right' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      allQualificationComponents: 'admin/qualificationComponents/all',
      institutions: 'institutions/all',
      profile: 'admin/profiles/find',
      qualificationCategories: 'qualificationCategories/all',
      qualificationTypes: 'qualificationTypes/all',
      qualifications: 'qualifications/all',
    }),
    filteredQualificationComponents() {
      return this.allQualificationComponents.filter(component => {
        if (this.profileQualification.institution && component.institution) {
          return component.institution.id === this.profileQualification.institution.id
        }
        if (this.profileQualification.qualificationType && component.qualificationType) {
          return component.qualificationType.id === this.profileQualification.qualificationType.id
        }
        return false
      })
    },
    filteredQualifications() {
      return this.qualifications.filter(q => {
        return (
          _.get(this.profileQualification, 'institution.id') === q.institution.id &&
          _.get(this.profileQualification, 'qualificationType.id') === q.qualificationType.id
        )
      })
    },
  },
  methods: {
    ...mapActions({
      removeComponent: 'admin/profileQualificationComponents/destroy',
      verify: 'admin/profileQualifications/verify',
    }),
    setQualification(qualification) {
      if (typeof qualification == 'object') {
        // Set Institution
        this.profileQualification.institution = qualification.institution
        this.profileQualification.institutionName = `${qualification.institution.name}`
        if (
          qualification.institution.id &&
          qualification.institution.gradeScale &&
          !this.profileQualification.gradeValue
        ) {
          this.profileQualification.gradeScale = value.gradeScale
        }

        // Set Type
        this.profileQualification.qualificationType = qualification.qualificationType
        this.profileQualification.qualificationTypeName = `${qualification.qualificationType.name}`
        this.profileQualification.specialization = `${qualification.specialization}`
        this.profileQualification.qualification = qualification
      }
    },
    setGrade({ gradeScale, gradeValue }) {
      this.profileQualification.gradeScale = gradeScale
      this.profileQualification.gradeValue = gradeValue
    },
    setComponentGrade({ gradeScale, gradeValue }) {
      this.componentBeingEdited.gradeScale = gradeScale
      this.componentBeingEdited.gradeValue = gradeValue
    },
    cancel() {
      this.editing = false
      this.$emit('refresh')
    },
    mergeProfileQualificationErrors(error) {
      const errorData = error && error.response && error.response.data ? error.response.data : error

      if (errorData && typeof errorData === 'object') {
        Object.assign(this.profileQualification, errorData)
      }
    },
    submit() {
      if (this.profileQualification.id) {
        this.$store.dispatch('admin/profileQualifications/update', this.profileQualification).then(
          // Success
          response => {
            this.editing = false
            this.$store.commit('snackbar', {
              text: 'Profile Qualification updated',
              type: 'success',
            })
            this.$store.dispatch('admin/profiles/fetch', this.$route.params.id)
          },
          // Failure
          error => {
            this.mergeProfileQualificationErrors(error)
            this.$store.commit('snackbar', {
              text: 'Profile Qualification could not be updated',
              type: 'error',
            })
          }
        )
      } else {
        this.$store
          .dispatch('admin/profileQualifications/create', this.profileQualification)
          .then(feedback => {
            if (feedback.errors) {
              this.$store.commit('snackbar', {
                text: 'There was a problem creating the profile qualification',
                type: 'error',
              })
              this.profileQualification.errors = feedback.errors
            } else {
              this.editing = false
              this.$store.commit('snackbar', {
                text: 'Profile Qualification was created successfully',
                type: 'success',
              })
            }
          })
      }
    },
    destroy() {
      this.$store
        .dispatch('admin/profileQualifications/destroy', this.profileQualification.id)
        .then(
          response => {
            this.$store.commit('snackbar', {
              text: 'Profile Qualification has been deleted',
              type: 'success',
            })
            this.$emit('refresh')
          },
          errors => {
            this.$store.commit('snackbar', {
              text: 'Profile Qualification could not be deleted',
              type: 'error',
            })
          }
        )
    },
    cancelComponentSearch() {
      this.componentSearch = ''
      this.addingComponent = false
    },
    createComponent(value) {
      if (typeof value == 'object') {
        this.$store.dispatch('admin/profileQualificationComponents/create', {
          profileQualification: { id: this.profileQualification.id },
          qualificationComponent: { id: value.id },
        })
      } else {
        // Description only
        this.$store.dispatch('admin/profileQualificationComponents/create', {
          profileQualification: { id: this.profileQualification.id },
          description: value,
        })
      }
      this.cancelComponentSearch()
    },
    updateComponent() {
      this.$store
        .dispatch('admin/profileQualificationComponents/update', this.componentBeingEdited)
        .then(invalid => {
          if (!invalid) this.componentBeingEdited = null
        })
    },
    setComponentDescription(component) {
      if (!this.componentBeingEdited.reference.length)
        this.componentBeingEdited.reference = component.reference
      if (!this.componentBeingEdited.description.length)
        this.componentBeingEdited.description = component.description
    },
  },
}
</script>
